import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { Wrench, ChevronDown, Minimize2 } from "lucide-react";

interface DevAutofillProps {
  templates: any[];
  selectedTemplateId: number | "";
  setClientName: (name: string) => void;
  setClientEmail: (email: string) => void;
  setInternalNote: (note: string) => void;
  setExpiresDays: (days: number) => void;
  setIsCustomizing: (val: boolean) => void;
  setActiveTab: (tab: "basics" | "builder") => void;
  schemaEditor: any;
  generatedLink: string | null;
}

const DEV_AUTOFILL_SAMPLES = [
  {
    name: "Fintech Payment Processor (Acme Pay)",
    industry: "Fintech & PCI",
    clientName: "Acme Pay Inc.",
    clientEmail: "compliance@acmepay.io",
    internalNote: "High priority payment gateway prospect seeking PCI-DSS compliant external API and core merchant portal scoping.",
    expiresDays: 14,
    schema: {
      sections: [
        {
          id: "sec_fintech_compliance",
          title: "1. Fintech & PCI Compliance Parameters",
          description: "Scoping parameters specifically focused on financial isolation and PCI-DSS compliance boundaries.",
          questions: [
            {
              qid: "pci_version",
              label: "Target PCI-DSS Compliance Version",
              type: "single_choice",
              required: true,
              options: [
                { value: "v4_0", label: "PCI-DSS v4.0 (Full Self-Assessment Questionnaire D)" },
                { value: "v3_2_1", label: "PCI-DSS v3.2.1 (Legacy Standard Compliance)" }
              ]
            },
            {
              qid: "cardholder_data",
              label: "Are cardholder PANs/CVVs stored in encrypted formats within your databases?",
              type: "yes_no",
              required: true
            },
            {
              qid: "api_authentication",
              label: "Primary API Gateway Authentication Protocol",
              type: "dropdown",
              required: true,
              options: [
                { value: "oauth2_mtls", label: "OAuth 2.0 with Mutual TLS (mTLS)" },
                { value: "api_keys", label: "Bearer API Keys / Secret Tokens" },
                { value: "jwt_stateless", label: "Signed JSON Web Tokens (JWT)" }
              ]
            }
          ]
        },
        {
          id: "sec_fintech_volume",
          title: "2. Transaction Volume & Core Integrations",
          description: "Details regarding integrations with other financial gateways and real-time peak transaction loads.",
          questions: [
            {
              qid: "third_party_gateways",
              label: "Does your system integrate with third-party payment processors (e.g. Stripe, Adyen)?",
              type: "yes_no",
              required: true
            },
            {
              qid: "peak_tps",
              label: "Estimated Peak Transactions Per Second (TPS)",
              type: "dropdown",
              required: true,
              options: [
                { value: "tps_under_100", label: "Under 100 TPS (Standard Retail)" },
                { value: "tps_100_1k", label: "100 - 1,000 TPS (High-Volume Merchant)" },
                { value: "tps_over_1k", label: "Over 1,000 TPS (Global Gateway / Enterprise)" }
              ]
            }
          ]
        },
        {
          id: "sec_fintech_threat",
          title: "3. Threat Model & Infrastructure Assets",
          description: "Database encryption schemes and target scopes in scope for the upcoming penetration test.",
          questions: [
            {
              qid: "db_hsm_encryption",
              label: "Is database encryption-at-rest configured using HSM-backed customer managed keys?",
              type: "yes_no",
              required: true
            },
            {
              qid: "business_assets",
              label: "Fintech Infrastructure Scopes (Target Builder)",
              type: "target_builder",
              required: true
            }
          ]
        }
      ]
    }
  },
  {
    name: "Healthcare Patient Portal (MedVitals EHR)",
    industry: "HealthTech & HIPAA",
    clientName: "MedVitals EHR Systems",
    clientEmail: "privacy-officer@medvitals.health",
    internalNote: "HIPAA compliant EHR patient portal. Assessment requires mobile applications (Android/iOS) + secure FHIR API endpoints validation.",
    expiresDays: 30,
    schema: {
      sections: [
        {
          id: "sec_healthcare_compliance",
          title: "1. HIPAA & ePHI Protection Scoping",
          description: "Identify electronic Protected Health Information (ePHI) processing nodes and FHIR interfaces.",
          questions: [
            {
              qid: "fhir_compliance",
              label: "Is your FHIR API backed by a certified health data broker?",
              type: "yes_no",
              required: true
            },
            {
              qid: "mobile_biometrics",
              label: "Does the Mobile application store offline patient data or leverage device-level biometrics?",
              type: "single_choice",
              required: false,
              options: [
                { value: "biometric_only", label: "Device Keychain + FaceID/Fingerprint only (No offline data)" },
                { value: "encrypted_sqlite", label: "Local Encrypted SQLite DB storing local health records" },
                { value: "no_local_storage", label: "No local storage — all data is fetched in real-time" }
              ]
            }
          ]
        },
        {
          id: "sec_healthcare_controls",
          title: "2. Audit Trail & Access Protocols",
          description: "Access security details and mandatory logging schemas to satisfy HIPAA regulations.",
          questions: [
            {
              qid: "audit_logging",
              label: "Select active HIPAA audit logging and monitoring targets",
              type: "multi_choice",
              required: true,
              options: [
                { value: "aws_cloudtrail", label: "AWS CloudTrail & GuardDuty Logs" },
                { value: "datadog_siem", label: "Datadog Security Monitoring (SIEM)" },
                { value: "application_level", label: "Application-level custom ePHI access tables" }
              ]
            },
            {
              qid: "mfa_clinical",
              label: "Is Multi-Factor Authentication (MFA) mandatory for all clinical and administrative roles?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_healthcare_phi",
          title: "3. Patient Data Safety & EHR Assets",
          description: "Verify patient health record protection policies and scope boundaries.",
          questions: [
            {
              qid: "phi_anonymization",
              label: "Is Patient Health Information (PHI) anonymized in non-production testing environments?",
              type: "yes_no",
              required: true
            },
            {
              qid: "business_assets",
              label: "EHR Scopes & Assets (Target Builder)",
              type: "target_builder",
              required: true
            }
          ]
        }
      ]
    }
  },
  {
    name: "E-Commerce Global Store (CartFly)",
    industry: "E-Commerce / Shopify Hybrid",
    clientName: "CartFly Retail Group",
    clientEmail: "ciso@cartfly.com",
    internalNote: "High-volume commerce platform. Hybrid custom checkout engine integrated with standard Shopify APIs. Needs massive payment web storefront test.",
    expiresDays: 45,
    schema: {
      sections: [
        {
          id: "sec_ecommerce_storefront",
          title: "1. E-Commerce Storefront Details",
          description: "Determine transaction scale and integration architecture.",
          questions: [
            {
              qid: "monthly_transactions",
              label: "Average Monthly Transaction Volume",
              type: "dropdown",
              required: true,
              options: [
                { value: "under_50k", label: "Under 50,000 transactions/month" },
                { value: "50k_500k", label: "50,000 to 500,000 transactions/month" },
                { value: "over_500k", label: "Over 500,000 transactions/month (High Load)" }
              ]
            },
            {
              qid: "custom_checkout",
              label: "Do you host a custom cart/checkout microservice, or redirect fully to Shopify Checkout?",
              type: "single_choice",
              required: true,
              options: [
                { value: "custom_microservice", label: "Custom microservice hosted on Kubernetes/AWS (Self-Managed)" },
                { value: "shopify_saas", label: "Fully redirected to Shopify Standard Checkout" }
              ]
            }
          ]
        },
        {
          id: "sec_ecommerce_security",
          title: "2. Payment Security & Platform Scale",
          description: "Anti-bot protection metrics and checkout endpoint methods.",
          questions: [
            {
              qid: "storefront_rate_limiting",
              label: "Are storefront API endpoints rate-limited against automated credential stuffing / scraping bots?",
              type: "yes_no",
              required: true
            },
            {
              qid: "checkout_methods",
              label: "Select payment gateway methods integrated into custom checkout",
              type: "multi_choice",
              required: true,
              options: [
                { value: "credit_card", label: "Direct Credit Card fields (Stripe Elements / custom token)" },
                { value: "mobile_wallets", label: "Mobile Wallet API (Apple Pay / Google Pay)" },
                { value: "bnpl_services", label: "Buy-Now-Pay-Later (Klarna / Affirm)" }
              ]
            }
          ]
        },
        {
          id: "sec_ecommerce_perf",
          title: "3. Performance Scale & Storefront Assets",
          description: "Determine load test requirements and target storefronts in scope.",
          questions: [
            {
              qid: "peak_load_testing",
              label: "Do you perform seasonal load testing prior to peak shopping events (e.g. Black Friday)?",
              type: "yes_no",
              required: true
            },
            {
              qid: "business_assets",
              label: "Global Storefront Target Scopes (Target Builder)",
              type: "target_builder",
              required: true
            }
          ]
        }
      ]
    }
  },
  {
    name: "Web3 DeFi Lending Protocol (SolLend)",
    industry: "Web3 & DeFi",
    clientName: "SolLend Labs",
    clientEmail: "dev@sollend.finance",
    internalNote: "DeFi Smart Contract audit for Solana lending protocol. Scoping both Rust programs and dApp React endpoints.",
    expiresDays: 15,
    schema: {
      sections: [
        {
          id: "sec_web3_smartcontract",
          title: "1. DeFi Smart Contract Parameters",
          description: "Configure smart contract compilation and target networks.",
          questions: [
            {
              qid: "blockchain_network",
              label: "Primary Deployment Network",
              type: "dropdown",
              required: true,
              options: [
                { value: "solana_mainnet", label: "Solana Mainnet (Rust Anchor Program)" },
                { value: "ethereum_l2", label: "Ethereum L2 (Arbitrum/Optimism - Solidity)" },
                { value: "evm_compatible", label: "EVM Compatible Custom Network (Avalanche/BSC)" }
              ]
            },
            {
              qid: "contract_lines",
              label: "Total Lines of Smart Contract Code (SLOC)",
              type: "single_choice",
              required: true,
              options: [
                { value: "under_1k", label: "Under 1,000 Lines (Simple ERC20/SPL Token)" },
                { value: "1k_5k", label: "1,000 – 5,000 Lines (Standard Lending/Staking Program)" },
                { value: "over_5k", label: "Over 5,000 Lines (Complex DAO or Multi-Signature vaults)" }
              ]
            }
          ]
        },
        {
          id: "sec_web3_integrations",
          title: "2. Wallet Integration & Upgradeability",
          description: "DeFi connectivity protocols and upgrade architecture.",
          questions: [
            {
              qid: "wallet_integrations",
              label: "Supported Web3 Wallets",
              type: "multi_choice",
              required: false,
              options: [
                { value: "metamask", label: "MetaMask / EVM Web3 Browsers" },
                { value: "phantom", label: "Phantom Wallet (Solana)" },
                { value: "ledger_hw", label: "Ledger / Trezor Hardware Wallets" }
              ]
            },
            {
              qid: "upgradeable_proxies",
              label: "Are smart contracts upgradeable using proxy patterns or multi-sig controllers?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_web3_architecture",
          title: "3. Protocol Security & Web3 Assets",
          description: "Formal verification metrics and dApp endpoint lists.",
          questions: [
            {
              qid: "formal_verification",
              label: "Has a formal mathematical verification or static analysis run been performed on the protocol core?",
              type: "yes_no",
              required: true
            },
            {
              qid: "business_assets",
              label: "dApp Web & Program Scopes (Target Builder)",
              type: "target_builder",
              required: true
            }
          ]
        }
      ]
    }
  },
  {
    name: "AI Automated Agent Platform (Anticognitive)",
    industry: "AI Startup & LLM Safety",
    clientName: "Anticognitive AI",
    clientEmail: "safety@anticognitive.ai",
    internalNote: "Autonomous AI agents executing custom code. Scoping prompt injection, LLM jailbreaks, and vector database API pentest.",
    expiresDays: 30,
    schema: {
      sections: [
        {
          id: "sec_ai_safety",
          title: "1. LLM & AI Safety Scoping",
          description: "Assess model interaction points and external API hooks for agent jailbreaks.",
          questions: [
            {
              qid: "model_hosting",
              label: "Primary Model Integration Type",
              type: "single_choice",
              required: true,
              options: [
                { value: "third_party_api", label: "Third-Party APIs (OpenAI GPT-4 / Anthropic Claude via SDK)" },
                { value: "self_hosted", label: "Self-Hosted Open Weights Models (Llama-3 / Mixtral on vLLM)" }
              ]
            },
            {
              qid: "rag_pipeline",
              label: "Does the system employ Retrieval Augmented Generation (RAG) using a vector database?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ai_sandbox",
          title: "2. Runtime Sandbox & Guardrails",
          description: "Code sandbox parameters and active prompt filtering technologies.",
          questions: [
            {
              qid: "sandbox_execution",
              label: "Are AI agents authorized to execute code in sandboxed runtime containers?",
              type: "single_choice",
              required: true,
              options: [
                { value: "gvisor_docker", label: "Yes — gVisor / Firecracker isolated Docker containers" },
                { value: "raw_shell", label: "Yes — standard local system shell execution (High Risk)" },
                { value: "no_execution", label: "No code execution allowed — pure text output" }
              ]
            },
            {
              qid: "prompt_guardrails",
              label: "Do you implement a prompt guardrail layer (e.g. Llama Guard, NeMo Guardrails) at inference?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_ai_data",
          title: "3. Vector Providers & AI Assets",
          description: "Data isolation metrics and vector storage endpoints in scope.",
          questions: [
            {
              qid: "pii_isolation",
              label: "Is training/fine-tuning data containing PII completely isolated from the inference pipeline?",
              type: "yes_no",
              required: true
            },
            {
              qid: "business_assets",
              label: "AI Endpoints & Agents (Target Builder)",
              type: "target_builder",
              required: true
            }
          ]
        }
      ]
    }
  },
  {
    name: "Enterprise SaaS HR Portal (StaffFlow)",
    industry: "Enterprise SaaS / B2B",
    clientName: "StaffFlow HR",
    clientEmail: "security-ops@staffflow.com",
    internalNote: "HR SaaS platform seeking ISO27001 compliance. Requires deep M365 tenant audit, external SSO integration audit, and full scope web application test.",
    expiresDays: 60,
    schema: {
      sections: [
        {
          id: "sec_enterprise_compliance",
          title: "1. Enterprise Security Controls",
          description: "SaaS scale options to gauge tenant configuration and compliance boundaries.",
          questions: [
            {
              qid: "active_users",
              label: "Total Active SaaS Platform Users",
              type: "dropdown",
              required: true,
              options: [
                { value: "under_10k", label: "Under 10,000 Users" },
                { value: "10k_100k", label: "10,000 to 100,000 Users" },
                { value: "over_100k", label: "Over 100,000 Users (Enterprise-scale)" }
              ]
            },
            {
              qid: "sso_protocols",
              label: "Supported SSO and Federation Protocols",
              type: "multi_choice",
              required: true,
              options: [
                { value: "saml2", label: "SAML 2.0 (Okta, Microsoft Entra ID)" },
                { value: "oidc", label: "OpenID Connect (OIDC)" },
                { value: "ldap_active_dir", label: "Direct Active Directory LDAP (Legacy)" }
              ]
            }
          ]
        },
        {
          id: "sec_enterprise_tenancy",
          title: "2. Tenant Isolation & Data Transits",
          description: "Assess logical separation between enterprise customer data.",
          questions: [
            {
              qid: "tenant_isolation",
              label: "Do you offer multi-tenant isolation at the database layer?",
              type: "single_choice",
              required: true,
              options: [
                { value: "shared_db_app_filter", label: "Shared Database - Isolated via App-level Tenant IDs" },
                { value: "postgres_rls", label: "Shared Database - Isolated via Postgres Row-Level Security (RLS)" },
                { value: "separate_databases", label: "Completely separate databases per customer tenant" }
              ]
            },
            {
              qid: "tls_requirements",
              label: "Is customer data encrypted in transit using TLS 1.3/1.2 only?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_enterprise_ops",
          title: "3. Operations Monitoring & SaaS Assets",
          description: "Identify threat detection systems and endpoints for testing.",
          questions: [
            {
              qid: "soc_monitoring",
              label: "Do you have a dedicated security operations team (SOC) monitoring logs 24/7?",
              type: "yes_no",
              required: true
            },
            {
              qid: "business_assets",
              label: "SaaS Platform Scopes (Target Builder)",
              type: "target_builder",
              required: true
            }
          ]
        }
      ]
    }
  },
  {
    name: "Autonomous Drone Delivery (AeroFast)",
    industry: "IoT Hardware & RF",
    clientName: "AeroFast Aviation",
    clientEmail: "hardware-ops@aerofast.aero",
    internalNote: "Scoping a drone aviation delivery software suite. Hardware physical attacks, firmware security, and specialized RF protocol communication review.",
    expiresDays: 30,
    schema: {
      sections: [
        {
          id: "sec_iot_hardware",
          title: "1. Hardware Interface & RF Scoping",
          description: "IoT physical debugging interfaces and RF telemetry parameters.",
          questions: [
            {
              qid: "physical_debugging",
              label: "Are JTAG, SWD, or UART serial ports physically accessible on the target hardware?",
              type: "yes_no",
              required: true
            },
            {
              qid: "rf_protocols",
              label: "Select RF Communications in Scope",
              type: "multi_choice",
              required: true,
              options: [
                { value: "lte_cellular", label: "LTE / 5G Cellular Backhaul" },
                { value: "custom_sub_ghz", label: "Custom Sub-GHz Proprietary Telemetry (FHSS)" },
                { value: "gps_gnss", label: "GNSS / GPS Navigation Frequencies" }
              ]
            }
          ]
        },
        {
          id: "sec_iot_firmware",
          title: "2. Firmware Safety & Local Storage",
          description: "Device cryptographic verification and flight log protections.",
          questions: [
            {
              qid: "firmware_signing",
              label: "Is the drone firmware signed with a secure boot key and verified by hardware?",
              type: "yes_no",
              required: true
            },
            {
              qid: "encrypted_telemetry",
              label: "Does the drone local storage encrypt flight logs, telemetry, and camera sensor feeds?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_iot_control",
          title: "3. Flight Range & Aviation Assets",
          description: "Operational range parameters and drone control scopes.",
          questions: [
            {
              qid: "operation_range",
              label: "Estimated remote pilot-in-command range (km) in operational fields",
              type: "dropdown",
              required: true,
              options: [
                { value: "range_short", label: "Short Range (Under 5 km)" },
                { value: "range_mid", label: "Medium Range (5 - 20 km)" },
                { value: "range_long", label: "Beyond Visual Line of Sight - BVLOS (Over 20 km)" }
              ]
            },
            {
              qid: "business_assets",
              label: "Hardware Device & Control Portals (Target Builder)",
              type: "target_builder",
              required: true
            }
          ]
        }
      ]
    }
  },
  {
    name: "EdTech K-12 Learning Platform (ClassBridge)",
    industry: "EdTech & COPPA Privacy",
    clientName: "ClassBridge Learning",
    clientEmail: "compliance@classbridge.edu",
    internalNote: "COPPA compliance assessment for a K-12 learning management platform. High sensitivity regarding student data privacy.",
    expiresDays: 30,
    schema: {
      sections: [
        {
          id: "sec_edtech_coppa",
          title: "1. COPPA & K-12 Student Privacy",
          description: "Verify consent records and student login federations.",
          questions: [
            {
              qid: "coppa_compliance",
              label: "Do you store verifiable parental consent records?",
              type: "yes_no",
              required: true
            },
            {
              qid: "student_sso",
              label: "Select Student SSO Providers",
              type: "multi_choice",
              required: true,
              options: [
                { value: "google_classroom", label: "Google Classroom Single Sign-On" },
                { value: "clever_edtech", label: "Clever EdTech portal" },
                { value: "local_credentials", label: "Direct Local Student Username/Password" }
              ]
            }
          ]
        },
        {
          id: "sec_edtech_moderation",
          title: "2. Moderation & Student Protections",
          description: "Protections configured for messaging features and search systems.",
          questions: [
            {
              qid: "chat_moderation",
              label: "Are chat and messaging features between students actively moderated?",
              type: "single_choice",
              required: true,
              options: [
                { value: "ai_moderated", label: "Real-time AI NLP filter (automute on flagged terms)" },
                { value: "human_moderated", label: "Post-facto human community moderator reviews" },
                { value: "no_moderation", label: "Unmoderated direct messaging (High COPPA risk)" },
                { value: "no_chat", label: "No direct chat or peer messaging features supported" }
              ]
            },
            {
              qid: "search_tracking",
              label: "Is student search activity logged and fully protected from advertising tracking?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_edtech_state",
          title: "3. Regional Accords & EdTech Assets",
          description: "Compliance validations for regional data contracts.",
          questions: [
            {
              qid: "state_data_agreements",
              label: "Do you comply with state-specific student data privacy agreements (e.g. SDPC / NDPA)?",
              type: "yes_no",
              required: true
            },
            {
              qid: "business_assets",
              label: "ClassBridge Platform Target Scopes (Target Builder)",
              type: "target_builder",
              required: true
            }
          ]
        }
      ]
    }
  },
  {
    name: "Logistics Supply Chain IoT (TrackSpeed)",
    industry: "Logistics & Embedded",
    clientName: "TrackSpeed Global",
    clientEmail: "firmware-sec@trackspeed.io",
    internalNote: "Fleet tracking IoT firmware security evaluation. Need deep firmware code review and API vulnerability scan.",
    expiresDays: 30,
    schema: {
      sections: [
        {
          id: "sec_logistics_firmware",
          title: "1. Logistics Firmware & Local Networks",
          description: "Specific details to define embedded tracker environment details.",
          questions: [
            {
              qid: "device_os",
              label: "Embedded Operating System",
              type: "single_choice",
              required: true,
              options: [
                { value: "freertos", label: "FreeRTOS or similar bare-metal real-time OS" },
                { value: "embedded_linux", label: "Embedded Yocto/Debian Linux" },
                { value: "zephyr", label: "Zephyr RTOS" }
              ]
            },
            {
              qid: "firmware_update_auth",
              label: "Are firmware updates cryptographically signed and verified by a hardware Root-of-Trust?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_logistics_wireless",
          title: "2. Wireless Triggers & Tamper Safes",
          description: "RF/sensor triggers and hardware defenses.",
          questions: [
            {
              qid: "wireless_protocols",
              label: "What local wireless protocols are used by tracker devices?",
              type: "multi_choice",
              required: true,
              options: [
                { value: "ble", label: "Bluetooth Low Energy (BLE)" },
                { value: "nfc_rfid", label: "Near-Field / RFID tag tracking" },
                { value: "lorawan", label: "LoRaWAN long-range low power telemetry" }
              ]
            },
            {
              qid: "tamper_wipe",
              label: "Is hardware tamper-detection configured to wipe encryption keys upon chassis opening?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_logistics_failsafe",
          title: "3. Buffering Fail-safes & Gateway Assets",
          description: "Local cache rules and backend tracking scopes.",
          questions: [
            {
              qid: "offline_buffer",
              label: "Is hardware tracking data buffered locally when cellular coverage is lost?",
              type: "yes_no",
              required: true
            },
            {
              qid: "business_assets",
              label: "IoT Tracker and Backend Gateways (Target Builder)",
              type: "target_builder",
              required: true
            }
          ]
        }
      ]
    }
  },
  {
    name: "Gaming & Metaverse Platform (NexusWorld)",
    industry: "Metaverse & High-Load APIs",
    clientName: "NexusWorld Interactive",
    clientEmail: "metaverse@nexusworld.gg",
    internalNote: "Metaverse virtual world backend API. Massive multi-player lobbies, custom payment microservices, and client game anti-cheat integration.",
    expiresDays: 21,
    schema: {
      sections: [
        {
          id: "sec_gaming_engine",
          title: "1. Real-Time Gaming APIs & Engines",
          description: "Choose client engine technologies and networking scales.",
          questions: [
            {
              qid: "game_engine",
              label: "Primary Client Game Engine",
              type: "single_choice",
              required: true,
              options: [
                { value: "unreal_engine", label: "Unreal Engine 5 (C++)" },
                { value: "unity_engine", label: "Unity Engine (C#)" },
                { value: "custom_webgl", label: "Custom WebGL / Three.js browser application" }
              ]
            },
            {
              qid: "lobby_scale",
              label: "Maximum Concurrent Lobby Connections per server node",
              type: "dropdown",
              required: true,
              options: [
                { value: "lobby_100", label: "Up to 100 players (Standard battle royale)" },
                { value: "lobby_1k", label: "Up to 1,000 players (Virtual concert / MMO)" },
                { value: "lobby_10k", label: "Over 10,000 players (Distributed virtual persistent world)" }
              ]
            }
          ]
        },
        {
          id: "sec_gaming_anticheat",
          title: "2. Game Economy & Anti-Cheat Controls",
          description: "Anti-cheat metrics and microtransaction security boundaries.",
          questions: [
            {
              qid: "anti_cheat_tool",
              label: "Select active anti-cheat/anti-tamper tools integrated into client",
              type: "multi_choice",
              required: true,
              options: [
                { value: "easy_anticheat", label: "Epic Easy Anti-Cheat (Kernel Driver)" },
                { value: "battleye", label: "BattlEye anti-cheat integration" },
                { value: "custom_obfuscation", label: "Proprietary memory obfuscation / Server-side validation" }
              ]
            },
            {
              qid: "in_game_purchases",
              label: "Does the game implement in-game purchase systems integrated with external payment services?",
              type: "yes_no",
              required: true
            }
          ]
        },
        {
          id: "sec_gaming_protocol",
          title: "3. Real-Time Traffic & Gaming Assets",
          description: "Define real-time networking protocols and api targets.",
          questions: [
            {
              qid: "realtime_protocol",
              label: "Primary network protocol for real-time game traffic",
              type: "dropdown",
              required: true,
              options: [
                { value: "udp_custom", label: "UDP custom protocol (Fast, unreliable delivery)" },
                { value: "tcp_websockets", label: "TCP Websockets (Reliable web browser lobbies)" },
                { value: "webrtc_stream", label: "WebRTC peer streaming (Real-time collaborative nodes)" }
              ]
            },
            {
              qid: "business_assets",
              label: "Gaming API Lobbies & Client Assets (Target Builder)",
              type: "target_builder",
              required: true
            }
          ]
        }
      ]
    }
  }
];

const DevAutofill: React.FC<DevAutofillProps> = ({
  templates,
  selectedTemplateId,
  setClientName,
  setClientEmail,
  setInternalNote,
  setExpiresDays,
  setIsCustomizing,
  setActiveTab,
  schemaEditor,
  generatedLink,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [devPanelPos, setDevPanelPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  
  // Ref to track coordinates during mousedown to distinguish drag from click
  const dragStartCoords = useRef({ x: 0, y: 0 });
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragTotalDistance = useRef(0);

  const handlePointerDown = (e: React.PointerEvent) => {
    // Only drag when clicking the handle or when clicking the collapsed bubble
    const target = e.target as HTMLElement;
    const isGrip = target.closest(".drag-handle");
    const isBubble = !isExpanded && target.closest(".dev-bubble");
    
    if (isGrip || isBubble) {
      setIsDragging(true);
      dragStartCoords.current = { x: e.clientX, y: e.clientY };
      dragOffset.current = {
        x: e.clientX - devPanelPos.x,
        y: e.clientY - devPanelPos.y,
      };
      dragTotalDistance.current = 0;
      
      // Capture pointer events on the target element
      target.setPointerCapture(e.pointerId);
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => {
      const dx = e.clientX - dragStartCoords.current.x;
      const dy = e.clientY - dragStartCoords.current.y;
      dragTotalDistance.current = Math.sqrt(dx * dx + dy * dy);

      setDevPanelPos({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };

    const handlePointerUp = (e: PointerEvent) => {
      setIsDragging(false);
      
      try {
        const target = e.target as HTMLElement;
        target.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Safe check
      }

      // If the cursor barely moved (less than 5px), treat it as a click toggle
      if (dragTotalDistance.current < 5) {
        setIsExpanded(prev => !prev);
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging, devPanelPos]);

  if (generatedLink) return null;

  return (
    <div
      style={{
        transform: `translate(${devPanelPos.x}px, ${devPanelPos.y}px)`,
        touchAction: "none",
      }}
      onPointerDown={handlePointerDown}
      className="fixed bottom-6 right-6 z-[999999] select-none shrink-0"
    >
      {!isExpanded ? (
        /* Collapsed Mini-Bubble FAB */
        <div
          className={`dev-bubble w-9 h-9 bg-gradient-to-tr from-indigo-600 to-violet-500 rounded-full flex items-center justify-center cursor-grab shadow-lg border border-indigo-400/30 hover:scale-105 active:scale-95 transition duration-150 relative ${
            isDragging ? "cursor-grabbing ring-2 ring-indigo-500/30" : ""
          }`}
          title="🛠️ Dev Wizard (Click to open, Drag to move)"
        >
          <Wrench size={13} className="text-white animate-pulse" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full ring-1 ring-white" />
        </div>
      ) : (
        /* Sleek, Ultra-Compact open Selector Widget */
        <div
          className={`bg-white/95 backdrop-blur-md border border-indigo-150 p-2 rounded-xl shadow-2xl w-[220px] flex flex-col gap-1.5 transition-all duration-200 ${
            isDragging ? "ring-2 ring-indigo-500/20 shadow-indigo-100/50" : ""
          }`}
        >
          {/* Header Drag Bar */}
          <div className="flex items-center gap-1 pb-1 border-b border-indigo-50/60 select-none shrink-0">
            <div 
              className={`drag-handle text-indigo-300 font-extrabold text-[10px] tracking-tight flex items-center justify-center mr-0.5 select-none ${
                isDragging ? "cursor-grabbing" : "cursor-grab"
              }`}
              title="Drag to reposition panel"
            >
              ⣿
            </div>
            <div className="flex items-center justify-center w-3.5 h-3.5 rounded bg-indigo-600 text-white shadow-sm shrink-0">
              <Wrench size={8} className="text-white" />
            </div>
            <div className="flex-1">
              <span className="font-extrabold text-indigo-950 text-[8.5px] leading-none block">🛠️ Dev Wizard</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
              }}
              className="w-4.5 h-4.5 p-0.5 flex items-center justify-center rounded text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition shrink-0"
              title="Minimize panel"
            >
              <Minimize2 size={8} />
            </button>
          </div>

          {/* Selector Dropdown */}
          <div className="relative">
            <select
              defaultValue=""
              onChange={(e) => {
                const idx = e.target.value;
                if (idx !== "") {
                  const sampleIdx = Number(idx);
                  const sample = DEV_AUTOFILL_SAMPLES[sampleIdx];
                  if (sample) {
                    // Fetch the currently selected template schema to merge
                    const activeTemplate = templates.find(t => t.id === selectedTemplateId) || templates[0];
                    let baseSchema: any = { sections: [] };
                    if (activeTemplate && activeTemplate.schema) {
                      try {
                        baseSchema = typeof activeTemplate.schema === "string" 
                          ? JSON.parse(activeTemplate.schema) 
                          : activeTemplate.schema;
                      } catch (err) {
                        console.error("Error parsing base template schema:", err);
                      }
                    }

                    // Deep-clone the base schema
                    const clonedBaseSchema = JSON.parse(JSON.stringify(baseSchema));
                    if (!clonedBaseSchema.sections) clonedBaseSchema.sections = [];
                    
                    // Collect all qids already in baseSchema
                    const existingQids = new Set<string>();
                    clonedBaseSchema.sections.forEach((sec: any) => {
                      (sec.questions || []).forEach((q: any) => {
                        if (q.qid) existingQids.add(q.qid.trim().toLowerCase());
                      });
                    });

                    // Check if base has a target_builder
                    const hasBaseTargetBuilder = clonedBaseSchema.sections.some((sec: any) =>
                      (sec.questions || []).some((q: any) => q.type === "target_builder")
                    );

                    // Deep-clone the sample schema
                    const sampleSchema = JSON.parse(JSON.stringify(sample.schema));
                    if (sampleSchema && sampleSchema.sections) {
                      // Append custom suffix to custom section IDs to avoid collisions
                      const timestamp = Date.now();
                      
                      const cleanSampleSections = sampleSchema.sections.map((sec: any, sIdx: number) => {
                        const cleanQuestions = (sec.questions || []).filter((q: any) => {
                          const qidClean = q.qid ? q.qid.trim().toLowerCase() : "";
                          if (existingQids.has(qidClean)) return false;
                          if (q.type === "target_builder" && hasBaseTargetBuilder) return false;
                          return true;
                        });
                        return {
                          ...sec,
                          id: sec.id ? `${sec.id}_custom_${timestamp}_${sIdx}` : `sec_custom_${timestamp}_${sIdx}`,
                          questions: cleanQuestions
                        };
                      }).filter((sec: any) => sec.questions.length > 0);

                      clonedBaseSchema.sections = [
                        ...clonedBaseSchema.sections,
                        ...cleanSampleSections
                      ];
                    }

                    // Populate basic fields
                    setClientName(sample.clientName);
                    setClientEmail(sample.clientEmail);
                    setInternalNote(sample.internalNote);
                    setExpiresDays(sample.expiresDays);
                    setIsCustomizing(true);

                    // Reset editor and focus on first of the newly appended custom sections
                    const baseSectionCount = baseSchema.sections?.length || 0;
                    schemaEditor.resetEditor(clonedBaseSchema, baseSectionCount);

                    // Switch tab to builder
                    setActiveTab("builder");

                    toast.success(`Loaded "${sample.name}" and merged with "${activeTemplate?.name || 'Base'}" template!`);
                    e.target.value = "";
                  }
                }
              }}
              className="w-full h-6.5 pl-1.5 pr-5 bg-white border border-indigo-150 rounded-lg text-[9px] font-bold text-indigo-900 focus:outline-none focus:border-indigo-500 hover:border-indigo-300 transition cursor-pointer shadow-sm appearance-none"
            >
              <option value="" disabled className="text-indigo-400">-- Choose Sample (10) --</option>
              {DEV_AUTOFILL_SAMPLES.map((sample, idx) => (
                <option key={idx} value={idx}>
                  {idx + 1}. {sample.name}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-1.5 flex items-center pointer-events-none text-indigo-500">
              <ChevronDown size={8} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevAutofill;
